import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Plus, Trash2, AlertCircle, Search, ChevronRight, Hash, Calendar,
} from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useSecurities, useUnmatchedSymbols } from '../hooks/useSecurities';

/**
 * Security identities -- the curated map of which ticker strings mean the same
 * company.
 *
 * The problem it solves: the app keys everything on the uppercase symbol, so a
 * company that trades under more than one is several unrelated records. Lanxess
 * is held at Schwab as the ADR LNXSY while Russ posts about LXS / LXS.DE, and
 * before this page existed clicking that holding produced a 404.
 *
 * Curation is deliberately manual. A wrong link fuses two companies' guidance
 * histories on one page, which is a far worse failure than an unlinked symbol.
 */
const Securities = () => {
  const { securities, loading, error, reload, save, remove, addAlias, removeAlias } = useSecurities();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  /**
   * Create the identity, then link its aliases. Two steps on the wire -- the
   * alias endpoint needs the identity to exist so it can reject an alias that
   * already resolves elsewhere -- but one step for the person doing it. The
   * two-step version of this flow (create, hunt for the card, click Edit, add
   * one alias, repeat) was undiscoverable.
   */
  const createIdentity = async ({ canonicalSymbol, displayName, aliases }) => {
    const pending = aliases || [];
    await save(canonicalSymbol, { displayName }, { reloadAfter: false });
    for (let i = 0; i < pending.length; i++) {
      // Sequential, not Promise.all: each alias is a read-modify-write of the
      // same document, so concurrent writes would lose all but one.
      // Only the last call refetches, so the list does not flicker per alias.
      await addAlias(canonicalSymbol, pending[i], { reloadAfter: i === pending.length - 1 });
    }
    if (pending.length === 0) await reload();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return securities;
    return securities.filter((s) =>
      s.canonicalSymbol?.toUpperCase().includes(q)
      || s.displayName?.toUpperCase().includes(q)
      || (s.aliases || []).some((a) => a.value?.toUpperCase().includes(q)));
  }, [securities, search]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Security Identities</h1>
          <p>
            Link the symbols that mean the same company. Russ refers to foreign names by
            their native listing (LXS, LXS.DE) while Schwab clears the ADR (LNXSY) &mdash;
            without a link here those never join.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          <Plus size={16} /> New identity
        </button>
      </div>

      {creating && (
        <NewIdentityForm
          onCancel={() => setCreating(false)}
          onCreate={async (fields) => {
            await createIdentity(fields);
            setCreating(false);
          }}
        />
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Search size={16} />
          <input
            type="text"
            className="settings-input"
            placeholder="Search symbol, name, or alias..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text-secondary)' }}>Loading identities...</p>}
      {error && <ErrorBlock message={error} />}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty-state">
          <Link2 size={48} />
          <p>{search ? `No identities matching "${search}"` : 'No identities curated yet'}</p>
          <p style={{ fontSize: '0.875rem' }}>
            The fastest way to add one is from a holding: open it in Portfolio, and if its
            detail page comes up empty you can link it there.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filtered.map((identity) => (
          <IdentityCard
            key={identity.canonicalSymbol}
            identity={identity}
            onSave={save}
            onDelete={remove}
            onAddAlias={addAlias}
            onRemoveAlias={removeAlias}
          />
        ))}
      </div>

      <UnmatchedPanel />
    </div>
  );
};

// --- One identity -----------------------------------------------------

const IdentityCard = ({ identity, onSave, onDelete, onAddAlias, onRemoveAlias }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const [aliasValue, setAliasValue] = useState('');
  const [aliasKind, setAliasKind] = useState('symbol');
  const [aliasExchange, setAliasExchange] = useState('');

  const [displayName, setDisplayName] = useState(identity.displayName || '');
  const [tradeableSymbol, setTradeableSymbol] = useState(identity.tradeableSymbol || '');

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      // The server explains alias conflicts precisely ("'LXS' already resolves
      // to X"); that text is the whole value of the response, so show it.
      setErr(e?.message?.replace(/^API call failed: /, '') || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => navigate(`/mentions/tickers/${encodeURIComponent(identity.canonicalSymbol)}`)}
          className="btn btn-secondary"
          style={{ fontWeight: 700, color: 'var(--primary-color)' }}
          title="Open this security's detail page"
        >
          {identity.canonicalSymbol} <ChevronRight size={14} />
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{identity.displayName || <em>No display name</em>}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.375rem' }}>
            {(identity.aliases || []).length === 0 && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                No aliases linked yet
              </span>
            )}
            {(identity.aliases || []).map((a) => (
              <span key={`${a.kind}:${a.value}`} className="badge" title={a.exchange || a.kind}>
                {a.value}
                {a.exchange ? ` · ${a.exchange}` : ''}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => onRemoveAlias(identity.canonicalSymbol, a.value))}
                  aria-label={`Unlink ${a.value}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    marginLeft: '0.25rem', padding: 0, color: 'inherit',
                  }}
                >
                  <Trash2 size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <button className="btn btn-secondary" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Done' : 'Edit'}
        </button>
      </div>

      {err && (
        <p style={{ color: 'var(--danger-color, #c0392b)', fontSize: '0.875rem', marginTop: '0.75rem' }}>
          <AlertCircle size={14} style={{ verticalAlign: 'text-bottom' }} /> {err}
        </p>
      )}

      {expanded && (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Display name" hint="Shown on the tickers list and detail page">
              <input
                className="settings-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Lanxess AG"
              />
            </Field>
            <Field label="Tradeable symbol" hint="What Schwab can quote; usually the canonical">
              <input
                className="settings-input"
                value={tradeableSymbol}
                onChange={(e) => setTradeableSymbol(e.target.value)}
                placeholder={identity.canonicalSymbol}
              />
            </Field>
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => run(() => onSave(identity.canonicalSymbol, { displayName, tradeableSymbol }))}
            >
              Save
            </button>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Add alias" hint="Another symbol or company name for this security">
              <input
                className="settings-input"
                value={aliasValue}
                onChange={(e) => setAliasValue(e.target.value)}
                placeholder="LXS.DE"
              />
            </Field>
            <Field label="Kind">
              <select
                className="settings-select"
                value={aliasKind}
                onChange={(e) => setAliasKind(e.target.value)}
              >
                <option value="symbol">Symbol</option>
                <option value="companyName">Company name</option>
              </select>
            </Field>
            <Field label="Exchange" hint="Optional">
              <input
                className="settings-input"
                value={aliasExchange}
                onChange={(e) => setAliasExchange(e.target.value)}
                placeholder="XETR"
                style={{ maxWidth: 110 }}
              />
            </Field>
            <button
              className="btn btn-secondary"
              disabled={busy || !aliasValue.trim()}
              onClick={() => run(async () => {
                await onAddAlias(identity.canonicalSymbol, {
                  value: aliasValue.trim(),
                  kind: aliasKind,
                  exchange: aliasExchange.trim() || undefined,
                });
                setAliasValue('');
                setAliasExchange('');
              })}
            >
              <Plus size={14} /> Link
            </button>
          </div>

          <div>
            <button
              className="btn btn-secondary"
              disabled={busy}
              style={{ color: 'var(--danger-color, #c0392b)' }}
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (!window.confirm(
                  `Delete the identity for ${identity.canonicalSymbol}? Its aliases stop resolving `
                  + 'and any documents filed under them go back to being separate. No mention data is lost.'
                )) return;
                run(() => onDelete(identity.canonicalSymbol));
              }}
            >
              <Trash2 size={14} /> Delete identity
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- New identity -----------------------------------------------------

const NewIdentityForm = ({ onCreate, onCancel }) => {
  const [canonicalSymbol, setCanonicalSymbol] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [aliasText, setAliasText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Preview of how each entry will be classified, so the inference is visible
  // before it is committed rather than surprising afterwards.
  const parsedAliases = useMemo(() => parseAliases(aliasText), [aliasText]);

  const submit = async () => {
    const symbol = canonicalSymbol.trim().toUpperCase();
    if (!symbol) return;
    setBusy(true);
    setErr(null);
    try {
      await onCreate({
        canonicalSymbol: symbol,
        displayName: displayName.trim() || undefined,
        aliases: parsedAliases,
      });
    } catch (e) {
      setErr(e?.message?.replace(/^API call failed: /, '') || 'Failed to create identity');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>New identity</h3>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        One company, every name it goes by. Start from the symbol your brokerage
        clears &mdash; it is the only one that can be priced, and it becomes this
        security&rsquo;s permanent address.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Field
            label="Your brokerage's symbol"
            hint="What Schwab shows in the account. Required."
          >
            <input
              className="settings-input"
              value={canonicalSymbol}
              onChange={(e) => setCanonicalSymbol(e.target.value)}
              placeholder="LNXSY"
            />
          </Field>
          <Field label="Company name" hint="Shown on the tickers list. Optional.">
            <input
              className="settings-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Lanxess AG"
            />
          </Field>
        </div>

        <Field
          label="Also known as"
          hint="What Russ calls it. Separate several with commas."
        >
          <input
            className="settings-input"
            value={aliasText}
            onChange={(e) => setAliasText(e.target.value)}
            placeholder="LXS, LXS.DE, Lanxess"
          />
        </Field>

        {parsedAliases.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Will link:</span>
            {parsedAliases.map((a) => (
              <span key={`${a.kind}:${a.value}`} className="badge">
                {a.value}
                <span style={{ opacity: 0.7 }}>
                  {a.kind === 'companyName' ? ' · name' : ' · symbol'}
                </span>
              </span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-primary" disabled={busy || !canonicalSymbol.trim()} onClick={submit}>
            Create
          </button>
          <button className="btn btn-secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      {err && (
        <p style={{ color: 'var(--danger-color, #c0392b)', fontSize: '0.875rem', marginTop: '0.75rem' }}>
          <AlertCircle size={14} style={{ verticalAlign: 'text-bottom' }} /> {err}
        </p>
      )}
    </div>
  );
};

/**
 * Split a comma-separated list into typed aliases.
 *
 * The kind is inferred rather than asked for. The distinction is a backend
 * detail -- company names are matched through a lossy slug and consulted last,
 * so they can never shadow a real ticker -- and nobody curating an identity
 * should have to think about it.
 *
 * Getting it wrong is cheap: a company name misfiled as a symbol still
 * resolves and still widens search, it just adds one pointless Cosmos
 * point-read to the detail page's fan-out. So the heuristic stays simple, the
 * preview shows what it decided, and the Edit panel has an explicit Kind
 * dropdown for the rare case that matters.
 */
const parseAliases = (text) => {
  const seen = new Set();
  return (text || '')
    .split(',')
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((value) => {
      const isName = looksLikeCompanyName(value);
      return { value: isName ? value : value.toUpperCase(), kind: isName ? 'companyName' : 'symbol' };
    })
    .filter((a) => {
      const key = a.value.toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/**
 * A space settles it ("Grab Holdings"). A dot settles it the other way -- that
 * is an exchange suffix, so "lxs.de" is a symbol however it was typed. What is
 * left: something over five characters containing a lowercase letter is a
 * name, because nobody types a ticker as "Lanxess" and real symbols are either
 * short or written in caps. Keeps LXS, lxs, LXS.DE, NVAAF, GOOGL as symbols.
 */
const looksLikeCompanyName = (value) => {
  if (value.includes(' ')) return true;
  if (value.includes('.')) return false;
  return value.length > 5 && /[a-z]/.test(value);
};

// --- Work queue -------------------------------------------------------

const UnmatchedPanel = () => {
  const { unmatched, loading, error } = useUnmatchedSymbols();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (loading || error || unmatched.length === 0) return null;

  const shown = expanded ? unmatched : unmatched.slice(0, 12);

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <h3 style={{ marginTop: 0 }}>Symbols with no identity ({unmatched.length})</h3>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        Most of these are plain US tickers that need nothing. The ones worth curating are
        foreign listings that pair with a holding you recognise.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {shown.map((u) => (
          <button
            key={u.symbol}
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(`/mentions/tickers/${encodeURIComponent(u.symbol)}`)}
            title={`${u.mentionCount} mention(s)`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
          >
            <span style={{ fontWeight: 600 }}>{u.symbol}</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <Hash size={10} /> {u.mentionCount}
            </span>
            {u.lastSeen && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <Calendar size={10} /> {formatDistanceToNowStrict(new Date(u.lastSeen), { addSuffix: true })}
              </span>
            )}
          </button>
        ))}
      </div>
      {unmatched.length > 12 && (
        <button className="btn btn-secondary" style={{ marginTop: '0.75rem' }} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show fewer' : `Show all ${unmatched.length}`}
        </button>
      )}
    </div>
  );
};

// --- Bits -------------------------------------------------------------

const Field = ({ label, hint, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 180px' }}>
    <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{label}</span>
    {children}
    {hint && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{hint}</span>}
  </label>
);

const ErrorBlock = ({ message }) => (
  <div className="empty-state">
    <AlertCircle size={48} />
    <p>{message}</p>
  </div>
);

export default Securities;
